import { useState, useEffect, useCallback } from 'react';
import { WebsiteWithConnections } from '../../types/website';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/dateUtils';
import DeleteConfirmationModal from '../clientDetail/sections/functions/DeleteConfirmationModal';
import AssignTaskDropdown from './AssignTaskDropdown';
import { Button, Badge, Card, Dropdown, Icon, IconButton, Input } from '../ui';
import type { BadgeTone } from '../ui';

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  due_date: string | null;
  priority: number;
  website_id: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  metadata: Record<string, unknown> | null;
}

interface TasksPanelProps {
  website: WebsiteWithConnections | null;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

const STATUS_TONES: Record<string, BadgeTone> = {
  Scheduled: 'info',
  'In Progress': 'warn',
  Complete: 'up',
  Blocked: 'down',
};

export default function TasksPanel({
  website,
  expandedSection,
}: TasksPanelProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    status: 'Scheduled',
    description: '',
    due_date: formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // Default to 1 week from now
    priority: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!website) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('website_tasks')
        .select('*')
        .eq('website_id', website.id)
        .order('priority', { ascending: true });

      if (error) throw error;

      // website_tasks rows lack the assignment fields this panel merges in from client_tasks below.
      const tasksData: Task[] = (data || []) as unknown as Task[];

      // Check for any tasks that have client task metadata
      const tasksWithClientData = await Promise.all(tasksData.map(async (task) => {
        // If the task has client_task_id in metadata, fetch the client task data
        const clientTaskId = task.metadata?.client_task_id;
        if (typeof clientTaskId === 'string') {
          try {
            const { data: clientTask, error: clientTaskError } = await supabase
              .from('client_tasks')
              .select('status, due_date, assigned_to, assigned_to_name')
              .eq('id', clientTaskId)
              .single();

            if (!clientTaskError && clientTask) {
              // Return task with client task data merged in
              return {
                ...task,
                status: clientTask.status ?? task.status,
                due_date: clientTask.due_date,
                assigned_to: clientTask.assigned_to,
                assigned_to_name: clientTask.assigned_to_name
              };
            }
          } catch (err) {
            console.error('Error fetching client task:', err);
          }
        }
        return task;
      }));

      setTasks(tasksWithClientData);
    } catch (err) {
      console.error('Error fetching website tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [website]);

  useEffect(() => {
    if (expandedSection === 'tasks' && website) {
      fetchTasks();
    }
  }, [expandedSection, website, fetchTasks]);

  const handleAddTask = async () => {
    if (!website || !newTask.title.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // First, add task to website_tasks
      const { data, error } = await supabase
        .from('website_tasks')
        .insert([{
          website_id: website.id,
          title: newTask.title,
          description: newTask.description || null,
          status: newTask.status,
          due_date: newTask.due_date || null,
          priority: tasks.length // Add to the end of the list
        }])
        .select();

      if (error) throw error;

      setTasks([...tasks, data[0] as unknown as Task]);
      setNewTask({
        title: '',
        description: '',
        status: 'Scheduled',
        due_date: formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        priority: 0
      });
      setIsAddingTask(false);
    } catch (err) {
      console.error('Error adding task:', err);
      setError('Failed to add task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignTask = async (taskId: string, userId: string, userName: string) => {
    try {
      setError(null);

      // Assignment lives on the linked client task; the function creates or updates it.
      const { error } = await supabase.rpc('assign_website_task_to_user', {
        p_website_task_id: taskId,
        p_user_id: userId,
      });

      if (error) throw error;

      // Update local state
      setTasks(tasks.map(task =>
        task.id === taskId ? {
          ...task,
          assigned_to: userId,
          assigned_to_name: userName
        } : task
      ));
    } catch (err) {
      console.error('Error assigning task:', err);
      setError('Failed to assign task. Please try again.');
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('website_tasks')
        .update({ status: newStatus })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(tasks.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      ));
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status.');
    }
  };

  const openDeleteModal = (taskId: string) => {
    setTaskToDelete(taskId);
    setDeleteModalOpen(true);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from('website_tasks')
        .delete()
        .eq('id', taskToDelete);

      if (error) throw error;

      setTasks(tasks.filter(task => task.id !== taskToDelete));
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task.');
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setTaskToDelete(null);
    }
  };

  return (
    <Card
      eyebrow="Tasks"
      title={`${tasks.filter(t => t.status !== 'Complete').length} open`}
      actions={
        !isAddingTask ? (
          <Button size="sm" icon="plus" onClick={() => setIsAddingTask(true)}>
            Add Task
          </Button>
        ) : undefined
      }
      style={{ overflow: 'visible' }}
    >
      {error && (
        <div
          style={{
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center" style={{ padding: 'var(--space-8) 0' }}>
          <div
            className="w-8 h-8 rounded-full animate-spin"
            style={{ border: '3px solid var(--border-hairline)', borderTopColor: 'var(--accent)' }}
          />
        </div>
      ) : (
        <>
          {isAddingTask && (
            <div
              className="ck-menu-pop"
              style={{
                marginBottom: 'var(--space-5)',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-4)',
              }}
            >
              <div className="flex justify-between items-center" style={{ marginBottom: 'var(--space-4)' }}>
                <span className="t-eyebrow">New Task</span>
                <IconButton icon="x" size="sm" label="Close" onClick={() => setIsAddingTask(false)} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <Input
                  label="Task Title*"
                  placeholder="Enter task title..."
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                />

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Description</div>
                  <textarea
                    className="ck-textarea"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Optional task description..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
                  <Dropdown
                    label="Status"
                    options={[
                      { value: 'Scheduled', label: 'Scheduled' },
                      { value: 'In Progress', label: 'In Progress' },
                      { value: 'Complete', label: 'Complete' },
                      { value: 'Blocked', label: 'Blocked' }
                    ]}
                    value={newTask.status}
                    onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
                    placeholder="Select status..."
                  />

                  <div>
                    <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Due Date</div>
                    <input
                      type="date"
                      className="ck-date-input"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end" style={{ gap: '8px', marginTop: 'var(--space-4)' }}>
                <Button size="sm" variant="ghost" onClick={() => setIsAddingTask(false)} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  icon="check"
                  onClick={handleAddTask}
                  disabled={isSubmitting || !newTask.title.trim()}
                >
                  {isSubmitting ? 'Saving...' : 'Add Task'}
                </Button>
              </div>
            </div>
          )}

          {tasks.length === 0 ? (
            <div className="text-center" style={{ padding: 'var(--space-6) 0', color: 'var(--text-faint)' }}>
              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                <Icon name="clipboard-check" size={20} />
              </div>
              <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>No tasks for this website yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className="hover-wash"
                  style={{
                    border: '1px solid var(--border-hairline)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 14px',
                    position: 'relative',
                  }}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between" style={{ gap: 'var(--space-3)' }}>
                    <div className="flex-1" style={{ minWidth: 0 }}>
                      <h4
                        style={{
                          margin: '0 0 8px',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                          color: task.status === 'Complete' ? 'var(--text-faint)' : 'var(--text-body)',
                          textDecoration: task.status === 'Complete' ? 'line-through' : 'none',
                        }}
                      >
                        {task.title}
                      </h4>
                      <div className="flex flex-wrap items-center" style={{ gap: '8px' }}>
                        <Badge tone={STATUS_TONES[task.status] || 'idle'} dot>{task.status}</Badge>

                        {task.due_date && (
                          <span
                            className="t-num"
                            style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                          >
                            <Icon name="calendar-days" size={12} />
                            Due {formatDate(task.due_date)}
                          </span>
                        )}

                        {task.assigned_to_name && (
                          <span
                            style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                          >
                            <Icon name="user" size={12} />
                            {task.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center" style={{ gap: '8px' }}>
                      {/* Status Dropdown */}
                      <Dropdown
                        size="sm"
                        options={[
                          { value: 'Scheduled', label: 'Scheduled' },
                          { value: 'In Progress', label: 'In Progress' },
                          { value: 'Blocked', label: 'Blocked' },
                          { value: 'Complete', label: 'Complete' }
                        ]}
                        value={task.status}
                        onChange={(e) => handleStatusChange(task.id, e.target.value)}
                        placeholder="Status..."
                        style={{ width: '132px' }}
                      />

                      <AssignTaskDropdown
                        taskId={task.id}
                        assignedUserId={task.assigned_to}
                        assignedUserName={task.assigned_to_name}
                        onAssign={(userId, userName) => handleAssignTask(task.id, userId, userName)}
                      />

                      <IconButton
                        icon="trash-2"
                        size="sm"
                        label="Delete Task"
                        onClick={() => openDeleteModal(task.id)}
                        style={{ color: 'var(--danger-400)' }}
                      />
                    </div>
                  </div>

                  {task.description && (
                    <div style={{ marginTop: '8px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                      {task.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteTask}
        isDeleting={isDeleting}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
      />
    </Card>
  );
}
